import { CSSProperties, useCallback, useRef, useState } from "react";

// Sr.No is fixed; other columns default to 260px within [COL_MIN, COL_MAX]. Row height grows the cells.
const SRNO_W = 76;
const COL_MIN = 130;
const COL_MAX = 1100;
const COL_DEFAULT = 260;
const ROW_MIN = 64;
const ROW_MAX = 260;
const ROW_DEFAULT = 64;

// Stable key for the Learner Name column in the width map.
export const NAME_COL = "__name__";

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

export interface GridResize {
  containerStyle: CSSProperties;
  srnoWidth: number;
  startColumnResize: (columnId: string, event: React.PointerEvent) => void;
  startRowResize: (event: React.PointerEvent) => void;
  getColumnWidth: (columnId: string) => number;
}

// Pointer-drag resizing; column widths keyed by column id, applied via <colgroup> for reliable reflow.
export const useGridResize = (): GridResize => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [rowHeight, setRowHeight] = useState(ROW_DEFAULT);
  const drag = useRef<{ axis: "x" | "y"; start: number; base: number; apply: (next: number) => void } | null>(null);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const state = drag.current;
    if (!state) return;
    const delta = (state.axis === "x" ? event.clientX : event.clientY) - state.start;
    state.apply(state.base + delta);
  }, []);

  const endDrag = useCallback(() => {
    drag.current = null;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    document.body.style.userSelect = "";
  }, [onPointerMove]);

  const beginDrag = useCallback(
    (axis: "x" | "y", base: number, apply: (next: number) => void, event: React.PointerEvent) => {
      event.preventDefault();
      drag.current = { axis, start: axis === "x" ? event.clientX : event.clientY, base, apply };
      document.body.style.userSelect = "none";
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", endDrag);
    },
    [onPointerMove, endDrag]
  );

  const getColumnWidth = useCallback((columnId: string) => columnWidths[columnId] || COL_DEFAULT, [columnWidths]);

  const startColumnResize = useCallback(
    (columnId: string, event: React.PointerEvent) => {
      const base = columnWidths[columnId] || COL_DEFAULT;
      beginDrag("x", base, next => setColumnWidths(prev => ({ ...prev, [columnId]: clamp(next, COL_MIN, COL_MAX) })), event);
    },
    [beginDrag, columnWidths]
  );

  const startRowResize = useCallback(
    (event: React.PointerEvent) => beginDrag("y", rowHeight, next => setRowHeight(clamp(next, ROW_MIN, ROW_MAX)), event),
    [beginDrag, rowHeight]
  );

  const containerStyle = {
    "--srno-w": `${SRNO_W}px`,
    "--row-h": `${rowHeight}px`,
    "--row-active-h": `${Math.max(93, rowHeight)}px`
  } as CSSProperties;

  return { containerStyle, srnoWidth: SRNO_W, startColumnResize, startRowResize, getColumnWidth };
};
